import { config } from '../config';
import { getApiToken } from '../auth/msal';
import { Perm } from './types';

/** Call the FABS admin/storage API with the current user's bearer token. */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getApiToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (init?.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${config.apiBase}${path}`, { ...init, headers });
  if (!res.ok) throw await responseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Build an Error from a failed Response, preferring the JSON `error` field. */
async function responseError(res: Response): Promise<Error> {
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    /* non-JSON error body */
  }
  return new Error(message);
}

/**
 * Get a short-lived, signed download URL for an object. Navigating an `<a download>` to it
 * streams the file through the browser's native download manager (with byte progress) —
 * unlike fetch-then-blob, which buffers the whole file first and shows no progress.
 */
export async function getDownloadUrl(bucket: string, key: string): Promise<string> {
  const res = await api<{ url: string }>(
    `/api/admin/buckets/${encodeURIComponent(bucket)}/download-url?key=${encodeURIComponent(key)}`,
  );
  return `${config.apiBase}${res.url}`;
}

/** PUT a File as an object body (with bearer auth) — for explorer uploads. */
export async function uploadObject(path: string, file: File): Promise<void> {
  const token = await getApiToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (file.type) headers['Content-Type'] = file.type;
  const res = await fetch(`${config.apiBase}${path}`, { method: 'PUT', body: file, headers });
  if (!res.ok) throw await responseError(res);
}

/** Safely extract a human-readable message from any thrown value. */
export function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * How the consent and Agents pages name an MCP agent: the name it registered with, if any, and
 * where it runs. Only the latter is vouched for: a client can call itself anything.
 */
export function agentLabel(agent: { clientName: string | null; redirectHost: string; loopback: boolean }): string {
  const where = agent.loopback ? 'an app on this computer' : agent.redirectHost;
  return agent.clientName ? `${agent.clientName} (${where})` : where;
}

/** Human label for a permission bitmask, e.g. 5 -> 'READ, LIST'. */
export function permLabel(perms: number): string {
  const keys = ['READ', 'WRITE', 'LIST', 'DELETE'] as const;
  return keys.filter((k) => (perms & Perm[k]) !== 0).join(', ') || '—';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}
